import { useState, useRef } from 'react';
import DropDownItem from './DropDownItem';
import DropDownAction from './DropDownAction';
import { Transition } from 'react-transition-group';
import {
  ItemDefinitionType,
  ActionDefinitionType,
} from '../types/DropDownTypes';

const duration = 300;

const defaultStyle = {
  transition: `opacity ${duration}ms ease-in-out`,
  opacity: 0,
  pointerEvents: 'none',
};

const transitionStyles: { [id: string]: React.CSSProperties } = {
  entering: { opacity: 1, pointerEvents: 'auto' },
  entered: { opacity: 1, pointerEvents: 'auto' },
  exiting: { opacity: 0, pointerEvents: 'none' },
  exited: { opacity: 0, pointerEvents: 'none' },
};

type DropDownProps<T extends string | number> = {
  items?: ItemDefinitionType<T>[];
  selected?: ItemDefinitionType<T>;
  actions?: ActionDefinitionType[];
  onChanged?: (item: ItemDefinitionType<T>) => void;
  isOpen: boolean;
  deselectable?: boolean;
  setIsOpen: () => void;
};

const HiddenDropDown = <T extends string | number>({
  items,
  selected,
  actions,
  onChanged,
  isOpen,
  deselectable = true,
  setIsOpen,
}: DropDownProps<T>): JSX.Element => {
  const initialState = {
    label: '',
    value: '',
  } as ItemDefinitionType<T>;

  const [currentSelected, setCurrentSelected] = useState(
    selected ? selected : initialState,
  );

  const clearSelectedItem = () => {
    setCurrentSelected(initialState);
    onChanged && onChanged(initialState);
  };

  const setSelectedItem = (item: ItemDefinitionType<T>) => {
    if (item.value === currentSelected.value) {
      if (!deselectable) return;
      clearSelectedItem();
    } else {
      setCurrentSelected(item);
      onChanged && onChanged(item);
    }

    setIsOpen();
  };

  const listItems = items
    ? items.map((item, idx) => {
        return (
          <DropDownItem
            item={item}
            key={`dropdown_${idx}`}
            isSelected={item.value === currentSelected.value}
            onClick={(item) => setSelectedItem(item)}
          />
        );
      })
    : null;

  const actionItems = actions
    ? actions.map((item, idx) => {
        return (
          <DropDownAction
            key={`action_${idx}`}
            item={item}
            onClicked={setIsOpen}
          />
        );
      })
    : null;

  const nodeRef = useRef(null);

  return (
    <div className={'relative select-none w-auto'}>
      <Transition in={isOpen} timeout={500} nodeRef={nodeRef}>
        {(state) => (
          <div
            className='absolute p-1 top-full mt-2 bg-white w-40 border border-gray-light border-opacity-20 rounded-lg shadow-xl z-30'
            style={{
              left: '-160px',
              ...defaultStyle,
              ...transitionStyles[state],
            }}
          >
            {items && listItems}
            {actions && actionItems}
          </div>
        )}
      </Transition>
    </div>
  );
};

export default HiddenDropDown;
